import Button from '@atlaskit/button';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import HomeIcon from '@atlaskit/icon/core/home';
import LinkIcon from '@atlaskit/icon/core/link';
import ShowMoreVerticalIcon from '@atlaskit/icon/core/show-more-vertical';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { messages } from '@databases/api-views/messages';
import DropdownMenu, {
  type CustomTriggerProps,
  DropdownItemGroup,
  type OnOpenChangeArgs,
} from '@databases/atlaskit/DropdownMenu';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { isStandalone } from '@databases/modes';
import { useComponent } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { type DatabaseView } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { showErrorSnackbar } from '@databases/utils/errors';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { ViewPanelSettingType } from '@databases/views/types';
import useCopyViewLink from '@databases/views/useCopyViewLink';
import useDeleteView from '@databases/views/useDeleteView';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { type ActionItem, DropdownItemWithTooltip } from './DropdownItemWithTooltip';
import { HoverButton } from './HoverButton';

export interface ViewActionsProps {
  contentId: string;
  view: DatabaseView;
  isViewActive?: boolean;
  isOpen: boolean;
  setIsViewActionsOpen: (isOpen: boolean) => void;
  isDefault: boolean;
  isReadonly?: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  handleCloseViewPicker?: () => void;
  isTabMode?: boolean;
  savedViews?: DatabaseView[];
  handleReorder?: (idx: number, newIdx: number) => void;
  isUnsavedView?: boolean;
  isRightmostTab?: boolean;
  rightmostTabIndex?: number;
}

const ViewActions: FunctionComponent<ViewActionsProps> = ({
  contentId,
  view,
  isViewActive,
  isOpen,
  setIsViewActionsOpen,
  isDefault,
  isReadonly,
  openViewSettingsPanel,
  handleCloseViewPicker,
  isTabMode = false,
  savedViews,
  handleReorder,
  isUnsavedView,
  isRightmostTab = false,
  rightmostTabIndex,
}) => {
  const client = useAnalytics();
  const { formatMessage } = useIntl();
  const { setViewId } = useView();
  const dispatch = defaultStoreUtils.useDispatch();
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');

  const database = defaultStoreUtils.useEqualSelector((state) =>
    selectDatabaseByContentIdRequired(state, { contentId })
  );

  const onCopyViewLink = useCopyViewLink(contentId);
  const onDeleteView = useDeleteView(contentId);

  const handleCopyViewLink = useCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsViewActionsOpen(false);

      const result = await onCopyViewLink(view);

      if (result) {
        client.sendViewLinkCopiedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          source: 'databaseViewActionsMenu',
          viewId: view.id,
        });
      } else {
        showErrorSnackbar(
          'failed-to-copy-error',
          formatMessage(messages['view-actions.failed-to-copy-error'])
        );
      }
    },
    [client, contentId, formatMessage, onCopyViewLink, setIsViewActionsOpen, view]
  );

  const handleSetDefaultView = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsViewActionsOpen(false);
      handleCloseViewPicker?.();

      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.SET_DEFAULT_SAVED_VIEW,
          viewId: view.id,
        })
      );

      currentSnackbarState.setMessage(
        formatMessage(messages['view-actions.views-make-default-flag-body'], {
          viewName: view.name,
        }),
        {
          id: 'default-view-changed',
          type: 'success',
          hideAfter: 3000,
          showCloseButton: true,
        }
      );

      client.sendDefaultViewSetEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'databaseViewActionsMenu',
        viewId: view.id,
      });
    },
    [
      client,
      contentId,
      dispatch,
      formatMessage,
      handleCloseViewPicker,
      setIsViewActionsOpen,
      view.id,
      view.name,
    ]
  );

  const handleAskRovo = useCallback(() => {}, []);

  const handleViewActionsOpenChange = useCallback(
    (attrs: OnOpenChangeArgs) => {
      attrs?.event?.stopPropagation();
      setIsViewActionsOpen(attrs.isOpen);
    },
    [setIsViewActionsOpen]
  );

  const handleOpenViewPanel = useCallback(() => {
    openViewSettingsPanel(ViewPanelSettingType.OVERVIEW);
    setViewId(view.id);
    handleCloseViewPicker?.();

    client.sendDatabaseEditViewEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'databaseViewActionsMenu',
      viewId: view.id,
    });
  }, [
    client,
    contentId,
    handleCloseViewPicker,
    openViewSettingsPanel,
    setViewId,
    view.id,
  ]);

  const TriggerComponent = isTabMode ? Button : HoverButton;
  const IconComponent = isTabMode ? ChevronDownIcon : ShowMoreVerticalIcon;

  const dropdownTrigger = useComponent(
    ({ triggerRef, ...props }: CustomTriggerProps) => {
      // The `Button` component does not support these props. They get passed through to the underlying
      // `<button>` DOM element, which results in a React warning
      const triggerComponentProps =
        TriggerComponent === Button
          ? {}
          : { $isActive: isViewActive, $isViewActionsOpen: isOpen };
      return (
        <TriggerComponent
          {...props}
          appearance="subtle"
          iconBefore={
            <IconComponent
              label={formatMessage(messages['view-actions.views-more-label'])}
            />
          }
          ref={triggerRef}
          spacing="compact"
          {...triggerComponentProps}
          isSelected={isTabMode && isOpen} // if it's not tab mode, we don't want to set this variable since we want to override the default selected styles
          className="view-actions-menu-trigger-button"
        />
      );
    },
    [TriggerComponent, IconComponent, formatMessage, isViewActive, isOpen, isTabMode]
  );

  const askRovoActions: Array<ActionItem> = useMemo(() => {
    if (!isDBAiFirstCreationEnabled || isReadonly) {
      return [];
    }

    return [
      {
        id: 'ask-rovo',
        name: formatMessage(messages['view-actions.ask-rovo']),
        onClick: handleAskRovo,
        icon: <AskRovoIcon height={16} width={16} />,
      },
    ];
  }, [formatMessage, handleAskRovo, isDBAiFirstCreationEnabled, isReadonly]);

  const viewActions: Array<ActionItem> = useMemo(() => {
    const editViewLabel = formatMessage(messages['view-actions.edit-view-text']);
    const makeDefaultLabel = formatMessage(
      messages['view-actions.views-make-default-text']
    );
    const copyLinkLabel = formatMessage(messages['view-settings.copy-link']);
    const deleteLabel = formatMessage(messages['view-actions.delete-view-delete-button']);

    return [
      !isReadonly && {
        id: 'edit-view',
        name: editViewLabel,
        onClick: handleOpenViewPanel,
        icon: <EditIcon spacing="spacious" label={editViewLabel} />,
      },
      isStandalone() &&
        !isUnsavedView &&
        !isReadonly && {
          id: 'make-default',
          name: makeDefaultLabel,
          onClick: handleSetDefaultView,
          icon: <HomeIcon spacing="spacious" label={makeDefaultLabel} />,
          disableConditions: [
            {
              isConditionActive: isDefault,
              message: formatMessage(
                messages['view-actions.views-make-default-tooltip-text']
              ),
            },
          ],
        },
      {
        id: 'copy-link',
        name: copyLinkLabel,
        onClick: handleCopyViewLink,
        icon: <LinkIcon spacing="spacious" label={copyLinkLabel} />,
      },
      database.views.length > 1 &&
        !isUnsavedView &&
        !isReadonly && {
          id: 'delete',
          name: deleteLabel,
          onClick: () => onDeleteView(view.id, 'databaseViewActionsMenu'),
          icon: <DeleteIcon spacing="spacious" label={deleteLabel} />,
        },
    ].filter(Boolean) as Array<ActionItem>;
  }, [
    database.views.length,
    formatMessage,
    handleCopyViewLink,
    onDeleteView,
    handleOpenViewPanel,
    handleSetDefaultView,
    isDefault,
    isReadonly,
    isUnsavedView,
    view,
  ]);

  const moveActions: Array<ActionItem> = useMemo(() => {
    const moveLeftLabel = formatMessage(messages['view-actions.move-left']);
    const moveRightLabel = formatMessage(messages['view-actions.move-right']);

    const viewIdx = savedViews?.findIndex((v) => v.id === view.id);
    const isFirst = viewIdx === 0;

    return isTabMode && !isUnsavedView && !isReadonly
      ? ([
          {
            id: 'move-left',
            name: moveLeftLabel,
            onClick: () =>
              viewIdx !== undefined &&
              handleReorder?.(
                viewIdx,
                (isRightmostTab && rightmostTabIndex ? rightmostTabIndex : viewIdx) - 1
              ),
            icon: <ArrowLeftIcon spacing="spacious" label={moveLeftLabel} />,
            disableConditions: [
              {
                isConditionActive: isFirst,
                message: formatMessage(messages['view-actions.move-left-tooltip-text']),
              },
            ],
          },
          {
            id: 'move-right',
            name: moveRightLabel,
            onClick: () => {
              return viewIdx !== undefined && handleReorder?.(viewIdx, viewIdx + 1);
            },
            icon: <ArrowRightIcon spacing="spacious" label={moveRightLabel} />,
            disableConditions: [
              {
                isConditionActive: isRightmostTab,
                message: formatMessage(messages['view-actions.move-right-tooltip-text']),
              },
            ],
          },
        ].filter(Boolean) as Array<ActionItem>)
      : [];
  }, [
    formatMessage,
    handleReorder,
    isReadonly,
    isRightmostTab,
    isTabMode,
    isUnsavedView,
    rightmostTabIndex,
    savedViews,
    view.id,
  ]);

  return (
    ((isTabMode && isViewActive) || !isTabMode) && (
      <DropdownMenu
        trigger={dropdownTrigger}
        isOpen={isOpen}
        onOpenChange={handleViewActionsOpenChange}
        testId="view-actions-dropdown-menu"
      >
        <DropdownItemGroup>
          {askRovoActions.map((item) => (
            <DropdownItemWithTooltip key={item.id} item={item} />
          ))}
        </DropdownItemGroup>
        <DropdownItemGroup hasSeparator>
          {viewActions.map((item) => (
            <DropdownItemWithTooltip key={item.id} item={item} />
          ))}
        </DropdownItemGroup>
        <DropdownItemGroup hasSeparator>
          {moveActions.map((item) => (
            <DropdownItemWithTooltip key={item.id} item={item} />
          ))}
        </DropdownItemGroup>
      </DropdownMenu>
    )
  );
};

export default ViewActions;
